import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InvoicesList from './InvoicesList';
import ViewInvoice from './ViewInvoice';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Invoices.css';

function SalesInvoices({ initialInvoiceId }) {
  const { t } = useTranslation('sales');
  const location = useLocation();
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'view', 'edit', 'create'

  // Handle initial invoice ID from cross-module navigation (/invoices/:id)
  useEffect(() => {
    if (initialInvoiceId) {
      setSelectedInvoiceId(parseInt(initialInvoiceId, 10));
      setViewMode('view');
    }
  }, [initialInvoiceId]);
  const [toolbarUpdateTrigger, setToolbarUpdateTrigger] = useState(0);

  // Handle direct navigation to specific invoice
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const invoiceId = urlParams.get('invoiceId');

    if (invoiceId && !selectedInvoiceId) {
      setSelectedInvoiceId(parseInt(invoiceId));
      setViewMode('view');
    }
  }, [location.search, selectedInvoiceId]);

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setInvoicesToolbar) {
      if (viewMode === 'list') {
        window.setInvoicesToolbar(
          <>
            <button className="btn-primary" onClick={handleNew}>
              <i className="icon">+</i> {t('invoices.toolbar.buttons.newInvoice')}
            </button>
            <button className="btn-secondary" onClick={handleRefresh}>
              <i className="icon">🔄</i> {t('invoices.toolbar.buttons.refresh')}
            </button>
          </>
        );
      } else {
        // No toolbar for view/edit/create modes
        window.setInvoicesToolbar(null);
      }
    }

    return () => {
      if (window.setInvoicesToolbar) {
        window.setInvoicesToolbar(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedInvoiceId]);

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedInvoiceId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewInvoice component
    if (window.viewInvoiceRef && window.viewInvoiceRef.handleDelete) {
      window.viewInvoiceRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewInvoice component
    if (window.viewInvoiceRef && window.viewInvoiceRef.handleSave) {
      window.viewInvoiceRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedInvoiceId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedInvoiceId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in InvoicesList
    if (window.invoicesListRef && window.invoicesListRef.fetchInvoices) {
      window.invoicesListRef.fetchInvoices();
    }
  };

  const handleInvoiceSaved = (invoice) => {
    setSelectedInvoiceId(invoice.invoice_id);
    setViewMode('view');
    // Refresh the list
    if (window.invoicesListRef && window.invoicesListRef.fetchInvoices) {
      window.invoicesListRef.fetchInvoices();
    }
  };

  const handleInvoiceDeleted = () => {
    setViewMode('list');
    setSelectedInvoiceId(null);
    // Refresh the list
    if (window.invoicesListRef && window.invoicesListRef.fetchInvoices) {
      window.invoicesListRef.fetchInvoices();
    }
  };

  return (
    <div className="invoices-page">
      <ResizablePanels
        leftPanel={
          <InvoicesList
            ref={ref => window.invoicesListRef = ref}
            onSelectInvoice={handleSelectInvoice}
            selectedInvoiceId={selectedInvoiceId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewInvoice
              ref={ref => window.viewInvoiceRef = ref}
              invoice={selectedInvoiceId ? { invoice_id: selectedInvoiceId } : null}
              mode={viewMode}
              onSave={handleInvoiceSaved}
              onCancel={handleCancel}
              onDelete={handleInvoiceDeleted}
              onBack={handleBack}
              onEdit={handleEdit}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default SalesInvoices;